import axios from 'axios';
import { IAuthResponse, ILoginCredentials } from '../interfaces/auth.interface';
import {ENDPOINTS} from "../constants/endpoints";
import { setImpersonationSwapInProgress } from './httpInterceptor';
import { getToken, setToken, clearToken } from './tokenStore';

const REMEMBERED_INSTITUTION_PREFIX = 'rememberedInstitution:';

// The refresh/logout/impersonate-exit endpoints are authenticated by the HttpOnly cookie alone.
// A custom header cannot be sent cross-site without a CORS preflight, which is the CSRF defence.
const CSRF_HEADERS = { 'X-Requested-With': 'runbizops' };
const COOKIE_REQUEST = { withCredentials: true, headers: CSRF_HEADERS };
// Refresh, logout and exit-impersonation must not carry the access token: it may be expired or revoked,
// and a bad bearer is rejected before the cookie is even looked at. `false` removes the axios default per request.
const COOKIE_ONLY_REQUEST = { withCredentials: true, headers: { ...CSRF_HEADERS, Authorization: false as const } };

// Tokens used to live in localStorage; remove what an older build left behind.
try {
    localStorage.removeItem('token');
    localStorage.removeItem('originalToken');
} catch {
    // Blocked storage: nothing to clean.
}

// Tabs share one refresh cookie, and rotation means only one of them may spend it at a time. They
// tell each other about the new access token (memory to memory, never storage) and about logout.
const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('runbizops-auth')
    : null;
const tokenWaiters = new Set<(token: string) => void>();

const clearLocalSession = () => {
    clearToken();
    localStorage.removeItem('selectedInstitutionId');
    localStorage.removeItem('selectedInstitutionCode');
};

if (channel) {
    channel.onmessage = (event: MessageEvent) => {
        const message = event.data as { type?: string; token?: string } | undefined;
        if (message?.type === 'token' && message.token) {
            setToken(message.token);
            tokenWaiters.forEach((resolve) => resolve(message.token as string));
        } else if (message?.type === 'logout') {
            clearLocalSession();
        }
    };
}

// A REFRESH_RACE rejection means another tab just spent the cookie; its new token arrives on the
// channel a moment later, so wait for it before treating the session as dead.
const waitForBroadcastToken = (ms: number): Promise<string | null> =>
    new Promise((resolve) => {
        const done = (token: string | null) => {
            tokenWaiters.delete(onToken);
            clearTimeout(timer);
            resolve(token);
        };
        const onToken = (token: string) => done(token);
        const timer = setTimeout(() => done(null), ms);
        tokenWaiters.add(onToken);
    });

const decodePayload = (token: string | null): any | null => {
    if (!token) return null;
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
};

const requestRefresh = async (): Promise<string | null> => {
    try {
        const response = await axios.post(ENDPOINTS.AUTH.REFRESH, null, COOKIE_ONLY_REQUEST);
        const token: string | undefined = response.data?.token;
        if (!token) {
            clearToken();
            return null;
        }
        setToken(token);
        channel?.postMessage({ type: 'token', token });
        return token;
    } catch (error: any) {
        if (error?.response?.status === 401) {
            if (error.response.data?.code === 'REFRESH_RACE') {
                const shared = await waitForBroadcastToken(2000);
                if (shared) return shared;
            }
            clearToken();
            return null;
        }
        throw error;
    }
};

let refreshInFlight: Promise<string | null> | null = null;

export const AuthService = {
    login: async (credentials: ILoginCredentials): Promise<IAuthResponse> => {
        try {
            const response = await axios.post(ENDPOINTS.AUTH.LOGIN, credentials, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'accept': '*/*'
                }
            });
            const data: IAuthResponse = response.data;
            if (data.status === 'AUTHENTICATED') {
                if (!data.token) {
                    throw new Error('No token received');
                }
                setToken(data.token);
            }
            return data;
        } catch (error: any) {
            console.error('Login error:', error);
            if (error.response) {
                const data = error.response.data;
                const status = error.response.status;
                const looksLikeHtml = typeof data === 'string' && /^\s*<(!doctype|html|head|body)/i.test(data);

                if (typeof data === 'string' && data.trim() && !looksLikeHtml) {
                    throw new Error(data);
                }
                if (data?.message) {
                    throw new Error(data.message);
                }
                if (status === 401 || status === 403) {
                    throw new Error('Invalid username or password.');
                }
                if (status >= 500 && status <= 599) {
                    throw new Error('The server is currently unavailable. Please try again in a few moments.');
                }
                if (status === 502 || status === 503 || status === 504) {
                    throw new Error('The server is currently unavailable. Please try again in a few moments.');
                }
                throw new Error('Login failed. Please try again.');
            }
            if (error.request) {
                throw new Error('Unable to reach the server. Please check your connection and try again.');
            }
            throw new Error('Login failed. Please try again.');
        }
    },

    verifyTwoFactor: async (challengeId: string, code: string, rememberDevice: boolean): Promise<IAuthResponse> => {
        try {
            const response = await axios.post(ENDPOINTS.AUTH.VERIFY_TWO_FACTOR, { challengeId, code, rememberDevice }, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'accept': '*/*'
                }
            });
            const data: IAuthResponse = response.data;
            if (data.status === 'AUTHENTICATED' && data.token) {
                setToken(data.token);
            }
            return data;
        } catch (error: any) {
            if (error.response?.data && typeof error.response.data === 'string') {
                throw new Error(error.response.data);
            }
            throw new Error('Invalid or expired code. Please try again.');
        }
    },

    // Spend the refresh cookie for a new access token. One call at a time per tab (single-flight)
    // and one per browser (Web Lock), because a rotated cookie can only be spent once. Resolves
    // null when there is no valid session; rejects only on a network or server failure.
    refresh: (): Promise<string | null> => {
        if (!refreshInFlight) {
            const run = (): Promise<string | null> => (typeof navigator !== 'undefined' && navigator.locks
                ? navigator.locks.request('runbizops-auth-refresh', requestRefresh)
                : requestRefresh());
            refreshInFlight = run().finally(() => {
                refreshInFlight = null;
            });
        }
        return refreshInFlight;
    },

    logout: () => {
        // M-028: no token survives in storage any more (the access token is in memory and the
        // refresh cookie is revoked server-side), so an impersonating SystemAdmin's session cannot
        // be picked up by the next person on this browser.
        Promise.resolve(axios.post(ENDPOINTS.AUTH.LOGOUT, null, COOKIE_ONLY_REQUEST))
            .catch(() => { /* the cookie also expires on its own; local state is cleared regardless */ });
        clearLocalSession();
        channel?.postMessage({ type: 'logout' });
    },

    // Set the selected institution ID in localStorage after checking approval status
    setSelectedInstitution: async (institutionId: string) => {
        try {
            // Fetch institution approval status using the dedicated endpoint
            const response = await axios.get(`${ENDPOINTS.INSTITUTION.BASE}/${institutionId}/is-approved`, {
                headers: AuthService.getAuthHeader()
            });

            const isApproved = response.data;

            // Check if institution is approved
            if (!isApproved) {
                // If not approved, logout and don't set the institution
                AuthService.logout();
                // Instead of throwing an error, return an object with error message
                return {
                    success: false,
                    error: 'Your institution is not approved yet. Please try again later.'
                };
            }

            // Check if institution is active
            const activeResponse = await axios.get(`${ENDPOINTS.INSTITUTION.BASE}/${institutionId}/is-active`, {
                headers: AuthService.getAuthHeader()
            });
            const isActive = activeResponse.data;

            if (!isActive) {
                AuthService.logout();
                return {
                    success: false,
                    error: 'Your institution has been deactivated. Please contact system administrator.'
                };
            }

            // If approved and active, store the institution ID
            localStorage.setItem('selectedInstitutionId', institutionId);
            await AuthService.cacheSelectedInstitutionCode(institutionId);
            return { success: true };
        } catch (error) {
            console.error('Error setting institution:', error);
            // Never persist a selection we could not verify (unapproved/inactive/unreachable institution).
            return {
                success: false,
                error: 'Error checking institution approval status.'
            };
        }
    },

    // Get the selected institution ID from localStorage
    getSelectedInstitution: () => {
        return localStorage.getItem('selectedInstitutionId')!!;
    },

    // Polish 4: a stored institution id that is not one of the signed-in user's institutions (a
    // previous user on this browser, a revoked membership, a demo reset) is dropped so the caller
    // falls back to the default pick. An empty list (a platform user) leaves the selection alone.
    // Returns true when a stale id was cleared.
    clearStaleSelectedInstitution: (institutionIds: string[] | undefined): boolean => {
        const stored = localStorage.getItem('selectedInstitutionId');
        if (!stored || !institutionIds || institutionIds.length === 0 || institutionIds.includes(stored)) {
            return false;
        }
        localStorage.removeItem('selectedInstitutionId');
        localStorage.removeItem('selectedInstitutionCode');
        return true;
    },

    // "Remember my choice" on the login institution picker: one institution per username, in this
    // browser only. Logout keeps it on purpose (that is the point of the option); keying by username
    // means another user on the same browser never inherits it. Storage errors just show the picker.
    getRememberedInstitution: (username: string | undefined): string | null => {
        if (!username) return null;
        try {
            return localStorage.getItem(REMEMBERED_INSTITUTION_PREFIX + username);
        } catch {
            return null;
        }
    },

    setRememberedInstitution: (username: string | undefined, institutionId: string | null) => {
        if (!username) return;
        try {
            if (institutionId) localStorage.setItem(REMEMBERED_INSTITUTION_PREFIX + username, institutionId);
            else localStorage.removeItem(REMEMBERED_INSTITUTION_PREFIX + username);
        } catch {
            // Blocked storage: the picker simply shows again next time.
        }
    },

    // Get the cached institution code (used by displayUsername and other prefix-aware helpers)
    getSelectedInstitutionCode: (): string | null => {
        return localStorage.getItem('selectedInstitutionCode');
    },

    setSelectedInstitutionCodeInStorage: (code: string | null) => {
        if (code) {
            localStorage.setItem('selectedInstitutionCode', code);
        } else {
            localStorage.removeItem('selectedInstitutionCode');
        }
    },

    // Fetch and cache the institution code for a given institution ID.
    // Non-fatal: clears the cache on failure so callers fall back to showing raw usernames.
    cacheSelectedInstitutionCode: async (institutionId: string): Promise<void> => {
        try {
            const currentUser = AuthService.getCurrentUser();
            if (!currentUser?.username) {
                AuthService.setSelectedInstitutionCodeInStorage(null);
                return;
            }
            const response = await axios.get(
                `${ENDPOINTS.INSTITUTION.BASE}/codes-by-username/${encodeURIComponent(currentUser.username)}`,
                { headers: AuthService.getAuthHeader() }
            );
            const codeMap = (response.data ?? {}) as Record<string, string>;
            AuthService.setSelectedInstitutionCodeInStorage(codeMap[institutionId] ?? null);
        } catch {
            AuthService.setSelectedInstitutionCodeInStorage(null);
        }
    },

    // Check if user needs to select an institution
    needsInstitutionSelection: (institutionIds: string[] | undefined) => {
        if (!institutionIds || institutionIds.length === 0) {
            return false; // No institutions to select
        }

        if (institutionIds.length === 1) {
            // For single institution, we'll handle auto-selection separately
            // since setSelectedInstitution is now async
            return false;
        }

        // Check if we've already selected an institution
        const selectedInstitution = AuthService.getSelectedInstitution();
        if (selectedInstitution && institutionIds.includes(selectedInstitution)) {
            return false; // Already has a valid selection
        }

        return true; // Multiple institutions, needs selection
    },

    getCurrentUser: () => {
        const token = getToken();
        if (token) {
            try {
                const base64Payload = token.split('.')[1];
                const payload = JSON.parse(atob(base64Payload));

                return {
                    token,
                    username: payload.sub,
                    roles: payload.roles,
                    name: payload.name,
                    issuedAt: payload.iat,
                    expiresAt: payload.exp,
                    issuer: payload.iss,
                    institutionIds: payload.institutionIds,
                    permissions: payload.permissions,
                    isDepartmentAdmin: payload.isDepartmentAdmin === true,
                    userId: payload.userId,
                    mustChangePassword: payload.mustChangePassword === true,
                };
            } catch (error) {
                console.error('Error parsing token:', error);
                return null;
            }
        }
        return null;
    },

    getAuthHeader: () => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    },

    isValidToken: () => {
        const token = getToken();
        if (!token) return false;

        try {
            const base64Payload = token.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));

            // Check if token is expired
            const currentTime = Math.floor(Date.now() / 1000);
            if (payload.exp && payload.exp < currentTime) {
                clearToken();
                return false;
            }

            return true;
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    },

    impersonate: async (userId: string): Promise<void> => {
        // D10: suppress the auth interceptor's logout-on-401/403 for the duration of the swap —
        // the old bearer can 403 mid-swap on a race with an in-flight request, which is not a
        // "your session died" signal. The admin's session stays on the server (impersonation is
        // recorded on the admin's refresh token), so exiting needs no parked token on the client.
        setImpersonationSwapInProgress(true);
        try {
            const response = await axios.post(ENDPOINTS.AUTH.IMPERSONATE(userId), null, COOKIE_REQUEST);
            setToken(response.data.token);
        } finally {
            setImpersonationSwapInProgress(false);
        }
    },

    exitImpersonation: async (): Promise<void> => {
        setImpersonationSwapInProgress(true);
        try {
            const response = await axios.post(ENDPOINTS.AUTH.IMPERSONATE_EXIT, null, COOKIE_ONLY_REQUEST);
            setToken(response.data.token);
            localStorage.removeItem('selectedInstitutionId');
            localStorage.removeItem('selectedInstitutionCode');
        } finally {
            setImpersonationSwapInProgress(false);
        }
    },

    isImpersonating: () => {
        return !!decodePayload(getToken())?.impersonatedBy;
    },
};
